import type { App } from 'vue';
import type { Router } from 'vue-router';

import { Device } from '@capacitor/device';
import { Filesystem } from '@capacitor/filesystem';
import { Intent, SendIntent } from '@mindlib-capacitor/send-intent';
import { useDocumentUploadStore } from 'src/stores/documentUpload';
import { useUserStore } from 'src/stores/user';

import { serializeActivityListDateRouteParam } from 'src/router/activityListDateRouteParam';

import useAuthentication from 'src/filters/authentication';

export interface IntentDataDetails {
	description?: string;
	title?: string;
	type?: string;
	url?: string;
}

export interface IntentData extends IntentDataDetails {
	additionalItems: Array<IntentDataDetails>;
}

export function bootSendIntent({ app, router }: { app: App<Element>; router: Router }): void {
	void Device.getInfo().then((deviceInfo) => {
		if (deviceInfo.platform === 'android' || deviceInfo.platform === 'ios') {
			SendIntent.checkSendIntentReceived()
				.then((result: Intent) => {
					const intendDataToStore: Array<IntentDataDetails> = [];
					if (result) {
						intendDataToStore.push({
							description: result.description,
							title: decodeURI(result.title ? result.title.replace(/^.*[\\/]/, '') : ''),
							type: result.type,
							url: result.url,
						});

						if (Object.prototype.hasOwnProperty.call(result, 'additionalItems')) {
							(result.additionalItems as Array<IntentDataDetails>).forEach((item) => {
								intendDataToStore.push({
									description: item.description,
									title: decodeURI(item.title ? item.title.replace(/^.*[\\/]/, '') : ''),
									type: item.type,
									url: item.url,
								});
							});
						}

						const { activeInstanceAuthenticationConfiguration } = useAuthentication();

						if (activeInstanceAuthenticationConfiguration.value !== null) {
							const commandBus = app.config.globalProperties.$commandBus;

							intendDataToStore.forEach((intendData) => {
								Filesystem.readFile({
									path: decodeURIComponent(<string>result.url),
								})
									.then((content) => {
										const filename = intendData.title ? intendData.title : '';

										useDocumentUploadStore().enqueue({
											authenticatedUserId: useUserStore().authenticatedUserInformation
												?.id as number,
											commandBus: commandBus,
											documentBoxId: 0,
											idoLinkings: [],
											sources: [
												{
													data: `data:${intendData.type as string};base64,${
														content.data as string
													}`,
													kind: 'base64',
													name: filename,
													type: intendData.type as string,
												},
											],
										});

										const targetDate = serializeActivityListDateRouteParam(
											new Date().toISOString().split('T')[0]
										);

										void Device.getInfo().then((deviceInfo) => {
											if (deviceInfo.platform === 'android') {
												SendIntent.finish();
											}
										});

										if (
											router.currentRoute.value.name === 'documentList' ||
											JSON.stringify(router.currentRoute.value.params) ===
												JSON.stringify({
													date: targetDate,
													source: 'main',
													view: 'open',
												})
										) {
											return;
										}

										void router.push({
											name: 'documentList',
											params: {
												date: targetDate,
												source: 'main',
												view: 'open',
											},
										});
									})
									.catch((err) => console.error(err));
							});
						}
					}
				})
				.catch((err) => console.error(err));
		}
	});
}
